import type { PropsWithChildren } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useReportFirstContactEventMutation } from "@/shared/api"
import { startOnboardingSession } from "@/shared/lib/onboarding-session"
import { setUsageChatId, startUsageTracking } from "@/shared/lib/usage-analytics"
import {
  parseStartParams,
  resolveTelegramRouteContext,
  RouterContext,
  setStoredChatId,
  TelegramContext,
  type StartParams
} from "@/shared/router"
import { WebApp } from "@/shared/telegram"

import { AppContext } from "./app-context"

import type { AppContextValue } from "./app-context"

interface AppProviderDiagnostics {
  isInitialized: boolean
  startParams: StartParams
  context: Pick<AppContextValue, "chatId" | "captchaMode" | "userId" | "shareToken">
  initializationError: string | null
}

declare global {
  interface Window {
    __celestiaAppProviderDiagnostics?: () => AppProviderDiagnostics
  }
}

export const AppProvider = ({ children }: PropsWithChildren) => {
  const [startParams] = useState<StartParams>(() => {
    const parsed = parseStartParams()
    // Bot-issued links explicitly carrying user_id win over the Telegram-provided
    // user.id (e.g. captcha-completion deep links that target a specific user).
    return {
      ...parsed,
      userId: parsed.userId ?? WebApp.initDataUnsafe.user?.id ?? null
    }
  })
  const hasCompleteCaptchaStartParams =
    startParams.captchaMode && startParams.chatId !== null && startParams.userId !== null
  const [chatId, setChatIdState] = useState<number | null>(() =>
    hasCompleteCaptchaStartParams ? startParams.chatId : null
  )
  const [devMode, setDevMode] = useState(() => (hasCompleteCaptchaStartParams ? startParams.devMode : false))
  const [isInitialized, setIsInitialized] = useState(hasCompleteCaptchaStartParams)
  const [tabParam, setTabParam] = useState<string | null>(() =>
    hasCompleteCaptchaStartParams ? startParams.tabParam : null
  )
  const [reportId, setReportId] = useState<string | null>(() =>
    hasCompleteCaptchaStartParams ? startParams.reportId : null
  )
  const [captchaMode, setCaptchaMode] = useState(() => hasCompleteCaptchaStartParams)
  const [userId, setUserId] = useState<number | null>(() => (hasCompleteCaptchaStartParams ? startParams.userId : null))
  const [autoMessageId, setAutoMessageId] = useState<number | null>(() =>
    hasCompleteCaptchaStartParams ? startParams.autoMessageId : null
  )
  const [shareToken, setShareToken] = useState<string | null>(() =>
    hasCompleteCaptchaStartParams ? startParams.shareToken : null
  )
  const [initializationError, setInitializationError] = useState<string | null>(null)

  const [reportFirstContactEvent] = useReportFirstContactEventMutation()
  const onboardingEventReported = useRef(false)

  const setChatId = useCallback(
    (newChatId: number | null) => {
      setChatIdState(newChatId)
      RouterContext.set(TelegramContext, { chatId: newChatId, shareToken })
      if (newChatId !== null) {
        setStoredChatId(newChatId).catch((error: unknown) => {
          console.warn("[AppProvider] Failed to save chatId to CloudStorage:", error)
        })
      }
    },
    [shareToken]
  )

  useEffect(() => {
    if (!hasCompleteCaptchaStartParams) {
      return
    }

    RouterContext.set(TelegramContext, {
      chatId: startParams.chatId,
      shareToken: startParams.shareToken
    })
  }, [hasCompleteCaptchaStartParams, startParams.chatId, startParams.shareToken])

  // Initialize app only once
  useEffect(() => {
    // Only run this effect once
    if (isInitialized) return

    const applyTelegramUiSettings = () => {
      try {
        WebApp.setHeaderColor("#111827")
        WebApp.setBackgroundColor("#111827")
        WebApp.setBottomBarColor("#111827")
        WebApp.enableClosingConfirmation()
        WebApp.disableVerticalSwipes()
      } catch (error: unknown) {
        console.warn("[AppProvider] Failed to apply Telegram UI settings:", error)
      }
    }

    const initializeApp = async () => {
      applyTelegramUiSettings()

      let finalChatId = startParams.chatId
      let finalShareToken = startParams.shareToken

      try {
        const resolvedContext = await resolveTelegramRouteContext(startParams, "AppProvider")
        finalChatId = resolvedContext.chatId
        finalShareToken = resolvedContext.shareToken
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Route context resolution failed"
        console.warn("[AppProvider] Failed to resolve Telegram route context:", error)
        setInitializationError(message)
      }

      RouterContext.set(TelegramContext, {
        chatId: finalChatId,
        shareToken: finalShareToken
      })

      // Update state with parsed values
      setChatIdState(finalChatId)
      setDevMode(startParams.devMode)
      setTabParam(startParams.tabParam)
      setReportId(startParams.reportId)
      setCaptchaMode(startParams.captchaMode)
      setUserId(startParams.userId ?? null)
      setAutoMessageId(startParams.autoMessageId ?? null)
      setShareToken(finalShareToken)

      // Mark initialization as complete
      setIsInitialized(true)
    }

    void initializeApp()
  }, [startParams, isInitialized])

  useEffect(() => {
    setUsageChatId(chatId)
  }, [chatId])

  useEffect(() => {
    if (isInitialized) startUsageTracking(startParams, chatId)
  }, [isInitialized, startParams, chatId])

  useEffect(() => {
    if (!isInitialized || onboardingEventReported.current) {
      return
    }

    const button = startParams.onboardingButton
    if (button !== "setup_moderation" && button !== "try_ai_shield") {
      return
    }

    onboardingEventReported.current = true
    const session = startOnboardingSession(button)
    reportFirstContactEvent({
      button,
      event: "feature_flow_opened",
      chatId,
      correlationId: session.token
    })
      .unwrap()
      .catch((error: unknown) => {
        console.warn("[AppProvider] Failed to report onboarding first-contact event:", error)
      })
  }, [isInitialized, startParams.onboardingButton, chatId, reportFirstContactEvent])

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return
    }

    window.__celestiaAppProviderDiagnostics = () => ({
      isInitialized,
      startParams,
      context: {
        chatId,
        captchaMode,
        userId,
        shareToken
      },
      initializationError
    })

    return () => {
      delete window.__celestiaAppProviderDiagnostics
    }
  }, [captchaMode, chatId, initializationError, isInitialized, shareToken, startParams, userId])

  const clearTabParam = useCallback(() => {
    setTabParam(null)
  }, [])

  const clearAutoMessageId = useCallback(() => {
    setAutoMessageId(null)
  }, [])

  // Provide value with initialization state
  const contextValue = useMemo<AppContextValue>(
    () => ({
      chatId,
      devMode,
      isInitialized,
      tabParam,
      reportId,
      captchaMode,
      joinRequestAttemptId: startParams.joinRequestAttemptId,
      userId: userId ?? null,
      autoMessageId,
      shareToken,
      setChatId,
      clearTabParam,
      clearAutoMessageId
    }),
    [
      chatId,
      devMode,
      isInitialized,
      tabParam,
      reportId,
      captchaMode,
      startParams.joinRequestAttemptId,
      userId,
      autoMessageId,
      shareToken,
      setChatId,
      clearTabParam,
      clearAutoMessageId
    ]
  )

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}
